// providerJourney -- the ONE answer to "is this provider set up, and what do
// they do next?". Pure: no React, no network. Every surface that used to keep
// its own idea of "complete" (the route guard, the Market Pulse lock, the
// profile page, the dashboard banners) reads it from here, so they can never
// disagree again.
//
// The journey is a small state machine:
//
//   profile -> services -> tour -> done
//
//   profile   a required profile step is unanswered (the one-question walk)
//   services  the profile is done and no service is live yet (services page)
//   tour      set up, and the dashboard tour has not been taken (3 stops)
//   done      the normal app
//   loading   an input is still unknown; show nothing journey-specific
//
// Why these steps and not fewer: the set is the union of what matching needs
// (backend provider-profile-completeness.util.ts: area, pay type + amount,
// work type, start date) and what services Go live refuses without
// (cardWalkModel.cardGuardsFor: the nine About-you halves). A provider who
// finishes the walk can therefore never hit a wall at Go live.

import { locationPreferenceFrom } from '../Schedule/locationPreference';

// The rule set's name. The backend keeps a line-for-line port of this module
// (provider-readiness/provider-journey.evaluator.ts) and both repos run the
// same case file (__fixtures__/provider-journey.cases.json). Changing a rule
// means changing both evaluators and both case files, and bumping this.
export const JOURNEY_RULES_VERSION = 'journey-v2';

// Older profiles store some multi-selects as a bare string ("Medical
// Direction"); both shapes count as answered.
export function asList(v) {
  if (Array.isArray(v)) return v.map((x) => String(x ?? '').trim()).filter(Boolean);
  if (typeof v === 'string' && v.trim()) return [v.trim()];
  return [];
}
const has = (v) => asList(v).length > 0;

export const isAnesthesiologist = (profile) => profile?.providerType === 'Anesthesiologist';

// CAAs have no RN or medical license field yet, and recruiter decks do not
// serve them (see project notes). Their licenses step is answered by default
// so the walk can never dead-end them.
export const isCAA = (profile) => String(profile?.providerType || '').toUpperCase() === 'CAA';

// SRNAs are students. Read from crnaCertificationStatus / isSRNA, never
// providerType (SRNAs are stored with providerType 'CRNA').
export const isStudent = (profile) =>
  !!profile?.isSRNA || profile?.crnaCertificationStatus === 'SRNA';

export function hasAnyLicense(profile) {
  return (
    has(profile?.stateLicenses) ||
    has(profile?.stateAPRNLicenses) ||
    has(profile?.stateMedicalLicenses)
  );
}

// Pay is answered only when the type the provider picked carries an amount.
// A legacy free-text `compensation.amount` does not count: the anonymous card
// and the services floor read hourlyAmount / salaryAmount only.
export function hasPayMinimum(profile) {
  const comp = profile?.compensation || {};
  const types = asList(comp.type).map((t) => t.toLowerCase());
  const hourly = types.includes('hourly') && Number(comp.hourlyAmount) > 0;
  const salary = types.includes('salary') && Number(comp.salaryAmount) > 0;
  return hourly || salary;
}

// Recruiter decks only show a provider whose certification status is Yes or
// SRNA (backend provider-profile-completeness.util.ts requires it for every
// provider type, Anesthesiologists included). Registration sets it; the
// licenses step re-sends isSRNA when it is missing, which makes the backend
// set it (provider.service.ts). Never asked on screen. CAAs are the one
// exception: recruiter decks do not serve them yet, so there is nothing to
// unlock and the walk must not dead-end them.
export function hasCertificationStatus(profile) {
  if (isCAA(profile)) return true;
  return ['Yes', 'SRNA'].includes(profile?.crnaCertificationStatus);
}

// Ordered so the provider is matchable after step 4 (where, work, pay,
// start are exactly what matching needs); a provider who stops after that
// still shows up in recruiter decks. `group` names the part of the walk.
export const PROFILE_STEPS = [
  {
    key: 'where',
    group: 'Your work',
    isDone: (p) => locationPreferenceFrom(p?.locationPreference) !== null,
  },
  {
    // Two short chip rows on one screen: the kind of work and W2 / 1099.
    key: 'work',
    group: 'Your work',
    isDone: (p) => has(p?.desiredWorkType) && has(p?.availability?.contractType),
  },
  { key: 'pay', group: 'Your work', isDone: hasPayMinimum },
  { key: 'start', group: 'Your schedule', isDone: (p) => !!p?.availability?.availabilityDate },
  {
    key: 'schedule',
    group: 'Your schedule',
    isDone: (p) =>
      has(p?.availability?.desiredSchedule) && has(p?.availability?.desiredShifts),
  },
  { key: 'certifications', group: 'Credentials', isDone: (p) => has(p?.activeCertifications) },
  {
    key: 'licenses',
    group: 'Credentials',
    isDone: (p) => isCAA(p) || (hasAnyLicense(p) && hasCertificationStatus(p)),
  },
  { key: 'cases', group: 'Clinical', isDone: (p) => has(p?.areasOfProficiency) },
  { key: 'interests', group: 'Clinical', isDone: (p) => has(p?.specialtyAreaOfInterest) },
  { key: 'emr', group: 'Clinical', isDone: (p) => has(p?.emrExperience) },
  {
    key: 'care',
    group: 'Clinical',
    isDone: (p) =>
      has(p?.facilityPreferences?.careTeamModel) &&
      has(p?.facilityPreferences?.preferredSettings),
  },
];

// The steps matching needs. Once these are answered the provider appears in
// recruiter decks even if they stop the walk there.
export const MATCHING_STEP_KEYS = ['where', 'work', 'pay', 'start'];

// The first value threshold: enough information to return an honest market
// result. This is deliberately NOT the eleven-step onboarding completion and
// must never be named `profileComplete`.
export function marketTermsStatus(profile) {
  const steps = PROFILE_STEPS.filter((step) => MATCHING_STEP_KEYS.includes(step.key));
  const missing = steps.filter((step) => !step.isDone(profile)).map((step) => step.key);
  return {
    complete: missing.length === 0,
    doneCount: steps.length - missing.length,
    total: MATCHING_STEP_KEYS.length,
    firstMissing: missing[0] || null,
    missing,
  };
}

export const PROFILE_STEP_KEYS = PROFILE_STEPS.map((s) => s.key);

export function profileSetupStatus(profile) {
  const total = PROFILE_STEPS.length;
  if (!profile) {
    return { complete: false, doneCount: 0, total, firstMissing: PROFILE_STEPS[0].key, missing: PROFILE_STEP_KEYS };
  }
  const missing = PROFILE_STEPS.filter((s) => !s.isDone(profile)).map((s) => s.key);
  return {
    complete: missing.length === 0,
    doneCount: total - missing.length,
    total,
    firstMissing: missing[0] || null,
    missing,
  };
}

export const isProfileSetupComplete = (profile) =>
  !!profile && profileSetupStatus(profile).complete;

// The next step to show after `fromKey`: the first UNANSWERED step after it,
// so a returning provider only walks what is missing. Null when nothing after
// it is missing (the walk is finished).
export function nextUnansweredStep(profile, fromKey) {
  const start = fromKey ? PROFILE_STEP_KEYS.indexOf(fromKey) + 1 : 0;
  for (let i = Math.max(start, 0); i < PROFILE_STEPS.length; i += 1) {
    if (!PROFILE_STEPS[i].isDone(profile)) return PROFILE_STEPS[i].key;
  }
  return null;
}

const DAY_MS = 24 * 60 * 60 * 1000;
export const TOUR_WINDOW_DAYS = 30;

// Onboarding (the services page and the tour) is offered only to accounts
// younger than the window, or to a provider who just arrived from the
// onboarding handoff (?tour=1). Veterans are never pulled back into it.
// Unknown creation time counts as a veteran. Use the ProviderProfile's
// createdAt: /auth/me does not return one.
export function isNewAccount(createdAt, now = new Date()) {
  if (!createdAt) return false;
  const t = new Date(createdAt).getTime();
  if (Number.isNaN(t)) return false;
  return now.getTime() - t < TOUR_WINDOW_DAYS * DAY_MS;
}

// Whether the walk asks a new account to confirm the licenses on file (the
// NPI lookup at registration fills them, so the licenses step is already
// answered and would otherwise never be shown). Accounts inside the
// onboarding window only, never a CAA, only when there is something to
// confirm, and only until the provider has confirmed once (the backend stamps
// licensesConfirmedAt). It only adds a walk screen: it is NOT part of
// isProfileSetupComplete, isRouteLocked or journeyStage, so it never re-locks
// or nudges anyone. `createdAt` defaults to the profile's own.
export function licensesToConfirm(profile, { createdAt = profile?.createdAt ?? null, now = new Date() } = {}) {
  if (!profile || isCAA(profile) || profile.licensesConfirmedAt) return false;
  // Nothing filled in: the licenses step asks the ordinary way.
  if (!hasAnyLicense(profile)) return false;
  return isNewAccount(createdAt, now);
}

const stateCode = (v) => String(v ?? '').trim().toUpperCase();

// Whether every license state on file is one the NPI registry lists, so the
// walk may say the states came from the NPI. Copy only: a state typed at the
// door or added later makes it false.
export function licensesFromNpi(profile) {
  const stored = [
    ...asList(profile?.stateLicenses),
    ...asList(profile?.stateAPRNLicenses),
    ...asList(profile?.stateMedicalLicenses),
  ].map(stateCode);
  if (!stored.length) return false;
  const taxonomies = Array.isArray(profile?.npi?.taxonomies) ? profile.npi.taxonomies : [];
  const onNpi = new Set(taxonomies.map((t) => stateCode(t?.state)).filter(Boolean));
  return stored.every((code) => onNpi.has(code));
}

// The screens a walk still has to show, in step order: every unanswered step,
// plus 'licenses' as a confirm when the walk carries one (`confirmLicenses`,
// latched by the walk when it starts on an unfinished profile) and
// licensesToConfirm says it is due.
export function walkPendingSteps(profile, { confirmLicenses = false, now = new Date() } = {}) {
  const p = profile || {};
  const confirm = confirmLicenses && licensesToConfirm(profile, { now });
  return PROFILE_STEPS.filter((s) => !s.isDone(p) || (confirm && s.key === 'licenses')).map((s) => s.key);
}

// The first pending screen after `fromKey` (from the start when null), or
// null when nothing after it is pending.
export function nextPendingStep(profile, fromKey, options = {}) {
  const pending = walkPendingSteps(profile, options);
  const from = fromKey ? PROFILE_STEP_KEYS.indexOf(fromKey) : -1;
  return pending.find((k) => PROFILE_STEP_KEYS.indexOf(k) > from) || null;
}

// How many screens the walk has left.
export const walkLeft = (profile, options = {}) => walkPendingSteps(profile, options).length;

// profileSetupStatus plus `walkLeft`: the screens a walk opened now would
// show. A walk started on an unfinished profile carries the licenses confirm,
// so the journey card and the walk's "{n} left" always agree.
export function walkSetupStatus(profile, { now = new Date() } = {}) {
  const status = profileSetupStatus(profile || null);
  return { ...status, walkLeft: walkLeft(profile, { confirmLicenses: !status.complete, now }) };
}

// The bar the app used to lock Discover, Vault and Services behind (the old
// five-section check). Kept only so the lock is never STRICTER than before
// for someone who already passed it: a provider who met the old bar but not
// the new one is nudged by the journey, not locked out.
function meetsLegacySetupBar(p) {
  if (!p) return false;
  const arrays = (v) => Array.isArray(v) && v.length > 0;
  const comp = p.compensation || {};
  return (
    !!p.locationPreference &&
    arrays(p.desiredWorkType) &&
    !!(comp.hourlyAmount || comp.salaryAmount || comp.amount) &&
    arrays(p.availability?.desiredSchedule) &&
    arrays(p.availability?.desiredShifts) &&
    !!p.availability?.availabilityDate &&
    arrays(p.areasOfProficiency) &&
    arrays(p.specialtyAreaOfInterest) &&
    arrays(p.emrExperience) &&
    arrays(p.facilityPreferences?.careTeamModel) &&
    arrays(p.facilityPreferences?.preferredSettings) &&
    arrays(p.activeCertifications) &&
    (arrays(p.stateLicenses) || arrays(p.stateAPRNLicenses) || arrays(p.stateMedicalLicenses))
  );
}

// Whether Discover, Vault and Services stay locked (the route guard and the
// Market Pulse lock). Locked only while the profile is unfinished by BOTH the
// new rule and the old one.
export function isRouteLocked(profile) {
  if (!profile) return true;
  return !isProfileSetupComplete(profile) && !meetsLegacySetupBar(profile);
}

/**
 * The journey stage.
 *
 * @param {object} input
 * @param {object|null|undefined} input.profile  ProviderProfile doc; undefined while loading
 * @param {boolean|null|undefined} input.servicesLive  a live service exists; null/undefined = unknown
 * @param {boolean} [input.servicesEnabled]  the services feature is on in this build
 * @param {boolean|null|undefined} input.tourDone  dashboard tour completed; null/undefined = unknown
 * @param {boolean|null} [input.servicesDeferred]  the provider chose "Not now" on the services page; null = unknown
 * @param {string|Date|null} [input.createdAt]  account creation time
 * @param {boolean} [input.tourRequested]  arrived with ?tour=1 from the handoff
 * @param {boolean} [input.failed]  the profile could not be loaded
 * @param {Date} [input.now]
 *
 * 'unknown' (failed) renders like 'done': fail open, never trap anyone.
 */
export function journeyStage({
  profile,
  servicesLive,
  servicesEnabled = true,
  tourDone,
  servicesDeferred = false,
  createdAt = null,
  tourRequested = false,
  failed = false,
  now = new Date(),
} = {}) {
  if (failed) return 'unknown';
  if (profile === undefined) return 'loading';
  if (!isProfileSetupComplete(profile)) return 'profile';

  // Past the profile, onboarding only runs for new accounts or a provider
  // who came from the handoff. Everyone else is simply done.
  if (!tourRequested && !isNewAccount(createdAt, now)) return 'done';
  // The tour is the last stop. Once taken, onboarding is over; a services
  // page the provider skipped comes back as an ordinary suggestion, never as
  // a step in front of the app.
  if (tourDone === true) return 'done';

  // A provider who said "Not now" to a services page moves on; the page is
  // offered again after the tour, never as a wall in front of it.
  // Students can set future services. CAAs do not have a services page yet:
  // services Go live still needs a license field CAAs do not have.
  const offerServices = servicesEnabled && !isCAA(profile);
  // null = the stored choice has not loaded yet; wait rather than flash the
  // services step at someone who already said Not now.
  if (offerServices && servicesDeferred === null) return 'loading';
  const needsServices = offerServices && !servicesDeferred;
  if (needsServices) {
    if (servicesLive === undefined || servicesLive === null) return 'loading';
    if (!servicesLive) return 'services';
  }

  if (tourDone === undefined || tourDone === null) return 'loading';
  return tourDone ? 'done' : 'tour';
}

// Whether going live on the services page hands the provider on to the
// tour: the stage the journey would be in now that the page is live (and so
// not deferred). A new account, or a provider who came through onboarding
// (`tourRequested`), with the tour known to be pending and a finished
// profile. Unknown answers hand on nobody.
export function tourFollowsGoLive({ profile, tourDone, createdAt = null, tourRequested = false, now = new Date() }) {
  return (
    journeyStage({
      profile,
      servicesLive: true,
      servicesDeferred: false,
      tourDone,
      createdAt,
      tourRequested,
      now,
    }) === 'tour'
  );
}

// Whether the journey could still be at the services step once its unknowns
// answer (the stored choices, whether a service is live): the stage with the
// answers most in favor of it. The services page waits on the journey only
// while this is true. Never for a veteran, an account of unknown age, a
// CAA, with services off, or while the profile is unfinished or unknown.
export function servicesStepPossible({ profile, createdAt = null, servicesEnabled = true, now = new Date() } = {}) {
  if (!profile) return false;
  return (
    journeyStage({
      profile,
      servicesLive: false,
      servicesDeferred: false,
      servicesEnabled,
      tourDone: false,
      createdAt,
      now,
    }) === 'services'
  );
}

// Where each stage's single primary goes. The dashboard card and any other
// "continue" surface use these, never their own routes.
export const JOURNEY_ROUTES = {
  profile: '/provider/profile?setup=1',
  services: '/provider/services?onboarding=profile',
  tour: '/provider/dashboard?tour=1',
};

// The route guard explains a bounce with the name of the place the provider
// was trying to open.
const GUARDED_PLACES = [
  ['/provider/discover', 'Discover'],
  ['/provider/vault', 'Vault'],
  ['/provider/services', 'Services'],
  ['/provider/rounds', 'Rounds'],
  ['/provider/roost', 'Rounds'],
];

export function guardedPlaceName(pathname) {
  const hit = GUARDED_PLACES.find(([prefix]) => String(pathname || '').startsWith(prefix));
  return hit ? hit[1] : null;
}
